<script lang="ts">
    import { fade } from 'svelte/transition';
    import {
        FollowButtonFragmentDoc,
        ParticipantAvatarFragmentDoc,
        type ParticipantCardFragment,
    } from '$gql/graphql.js';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import Spinner from '$components/Spinner/Spinner.svelte';
    import {
        participantIsCompany,
        participantIsUser,
        participantSveltekitReload,
    } from '$lib/utils/participantUtils';
    import FollowButton from '$components/FollowButton/FollowButton.svelte';
    import ParticipantAvatar from '../ParticipantAvatar/ParticipantAvatar.svelte';
    import CountryPicture from '$components/CountryPicture.svelte';
    import { useFragment } from '$gql/fragment-masking.js';
    import { createLinkPreview, melt } from '@melt-ui/svelte';
    import type { FloatingConfig } from '@melt-ui/svelte/internal/actions';
    import { safe } from '$lib/utils/string';
    import { keepNotEmptyString } from '$lib/utils/filterNotNull';
    import { match } from 'ts-pattern';

    export let participant: ParticipantCardFragment;
    export let loading: boolean = false;

    export let positioning: FloatingConfig = {
        placement: 'bottom',
        gutter: 10,
        strategy: 'fixed',
    };

    let className = 'inline-block';
    export { className as class };

    const intl = getContainer().intl();

    const {
        elements: { trigger, content, arrow },
        states: { open },
    } = createLinkPreview({
        openDelay: 500,
        closeDelay: 500,
        positioning,
        forceVisible: false,
    });

    $: isMe = false;

    /*$: isMe =
        me == null
            ? false
            : participantInputAreEqual(participantId, participantToInput(me));*/

    $: category = match(participant)
        .with({ __typename: 'Company' }, ({ companyCategory }) => {
            const parentName = companyCategory.parent?.category;
            const categoryName = companyCategory.category;

            return safe`${parentName ? parentName + ' / ' : ''}${categoryName}`;
        })
        .otherwise(() => '');
    $: country = participant?.participantCountry ?? null;
    $: city = participant?.participantCity ?? null;
    $: countryCity = keepNotEmptyString([
        country == null ? null : intl.countryLabel(country),
        city,
    ]).join(' / ');

    $: sveltekitReload = participantSveltekitReload(participant);
</script>

<div class={className} use:melt={$trigger}>
    <slot />
</div>

{#if $open && participant != null}
    <div
        use:melt={$content}
        class="z-popover fixed shadow-md rounded-lg bg-white p-10 border border-gray-300 max-w-[500px]"
        in:fade={{ duration: 100 }}
    >
        {#if loading}
            <Spinner size="sm" />
        {/if}

        <div class={'flex gap-4'}>
            <div
                class={'shrink-0 force-size-[80px] border border-gray-300 shadow p-1 rounded-full'}
            >
                <ParticipantAvatar
                    participant={useFragment(ParticipantAvatarFragmentDoc, participant)}
                    size={'full'}
                />
            </div>
            <div class={'flex-grow overflow-hidden'}>
                <a
                    href={participant.participantProfileLink}
                    data-sveltekit-reload={sveltekitReload}
                    target={'_blank'}
                    class={'block text-20 text-link cursor-pointer font-bold truncate'}
                >
                    {participant.participantName}
                </a>
                <div class={'text-gray-600 truncate'}>
                    {participantIsUser(participant)
                        ? (participant.participantTitle ?? '')
                        : participantIsCompany(participant)
                          ? (category ?? '')
                          : ''}
                </div>
                {#if !isMe}
                    <div class={'flex items-center gap-4 mt-4'}>
                        <FollowButton
                            target={useFragment(FollowButtonFragmentDoc, participant)}
                            class="btn-leo btn-sm"
                            spinnerClass="text-inherit"
                            followedClassName="btn-leo-blue"
                            unfollowedClassName="btn-leo-outline-blue"
                        />
                    </div>
                {/if}
            </div>
        </div>
        <div class={'flex items-center gap-2 mt-8'}>
            <span>
                <span class={'font-bold'}>{participant.followersCount}</span>&nbsp;
                <!--                 {transChoice("feeds.participant_card.followers", participant.followersCount)} -->
                abonnés
            </span>
            <span class={'text-gray-500'}>&bull;</span>
            {#if participantIsUser(participant)}
                <span>
                    <span class={'font-bold'}>{participant.followingCount}</span>&nbsp;
                    <!-- {transChoice("feeds.participant_card.following", participant.followingCount)} -->
                    abonnements
                </span>
            {/if}

            {#if participant.__typename === 'Company'}
                <span>
                    <span class={'font-bold'}>{participant.nbVisits}</span>&nbsp;
                    <!-- {transChoice("feeds.participant_card.views", participant.nbVisits)} -->
                    vues
                </span>
            {/if}

            <span class={'text-gray-500'}>&bull;</span>
            <span>
                <span class={'font-bold'}>{participant.feedPostsCount}</span>&nbsp;
                <!-- {transChoice("feeds.participant_card.posts", participant.feedPostsCount)} -->
                posts
            </span>
        </div>
        <div class={'flex items-center mt-2'}>
            {#if country != null}
                <CountryPicture class={'mr-2'} size="24" {country} />
            {/if}
            {#if countryCity != null}
                <span class={'text-sm text-gray-600'}>
                    {countryCity}
                </span>
            {/if}
        </div>

        <div use:melt={$arrow} />
    </div>
{/if}
